import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar1 } from '../components/shadcnblocks-com-navbar1';
import { AuroraBackground } from '../components/ui/aurora-background';
import { TextRotate } from '../components/ui/text-rotate';
import { motion, LayoutGroup, useReducedMotion } from 'framer-motion';
import { Footer } from '../components/ui/footer';
import Faq2 from '../components/mvpblocks/faq-2';
import { planesDeContratacion } from '../data/planes';
import TarjetaDePlan from '../components/business/TarjetaDePlan';
import MosaicoFuncionalidades from '../components/business/MosaicoFuncionalidades';
import PanelEnPerspectiva from '../components/business/PanelEnPerspectiva';
import Aparecer from '../components/business/Aparecer';
// Mismos rubros que usa el onboarding, para que la landing no prometa algo
// distinto de lo que después ofrece el producto.
import { rubros, rubroEmojis } from '../data/rubros';
import { footerData } from '../data/navegacion';
// Las mismas preguntas que muestra el bloque de FAQ. Antes había una lista acá
// para el schema de SEO y otra distinta adentro del componente: Google indexaba
// respuestas que el usuario no veía en la página.
import { faqs } from '../data/faqs';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import useScrollSuave from '../hooks/useScrollSuave';
import { organizationSchema, softwareApplicationSchema, webSiteSchema, serviceSchema, faqSchema } from '../utils/schemas';
import './Home.css';

// Los botones de la landing son el <Button> de la app con size="cta": el color y
// el redondeado viven en components/ui/button.jsx, no acá. Antes esta página
// tenía sus propias clases con el naranja escrito a mano, y por eso los botones
// de la landing y los del resto de la app no coincidían.

/**
 * Emojis de rubro flotando detrás del hero.
 *
 * Salen de la misma lista que usa el onboarding, así que representan
 * exactamente los negocios que la app soporta. Es decoración: aria-hidden y
 * pointer-events-none para que no molesten a lectores de pantalla ni
 * intercepten clicks, y quedan quietos si el sistema pide menos movimiento.
 *
 * Tres por lado, en las bandas laterales, esquivando la columna del contenido,
 * con posiciones fijas (no aleatorias) para que el render sea estable. El hero
 * es max-w-3xl (768px) centrado, así que en xl (1280px) ocupa del 20% al 80% y
 * quedan libres 0-19% y 81-100%: por eso aparecen recién en xl, abajo de eso el
 * contenido es casi todo el ancho y se superpondrían.
 */
const POSICIONES = [
  { left: '4%', top: '16%', delay: 0 },
  { left: '12%', top: '48%', delay: 1.6 },
  { left: '5%', top: '79%', delay: 3.1 },
  { left: '87%', top: '11%', delay: 0.8 },
  { left: '93%', top: '44%', delay: 2.4 },
  { left: '84%', top: '75%', delay: 3.9 },
];

const EmojisFlotantes = () => {
  const menosMovimiento = useReducedMotion();

  return (
    <div className="pointer-events-none absolute inset-0 hidden select-none overflow-hidden xl:block" aria-hidden="true">
      {rubroEmojis.slice(0, POSICIONES.length).map((emoji, i) => (
        <motion.span
          key={emoji}
          className="emoji-flag absolute text-[56px] opacity-40"
          style={{ left: POSICIONES[i].left, top: POSICIONES[i].top }}
          animate={menosMovimiento ? undefined : { y: [0, -16, 0] }}
          transition={
            menosMovimiento
              ? undefined
              : { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: POSICIONES[i].delay }
          }
        >
          {emoji}
        </motion.span>
      ))}
    </div>
  );
};

/* CheckIcon se mudó a components/business/TarjetaDePlan.jsx, que es el único
   lugar que lo usaba desde que la tarjeta de plan es compartida. */

const StarIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-primary" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const Home = () => {
  const navigate = useNavigate();
  const { currentUser, userData, loading } = useAuth();

  // Inercia al scrollear. Solo en esta página y en la pública: el panel y el
  // onboarding tienen contenedores con scroll propio que se traban con esto.
  const scrollSuave = useScrollSuave();

  // Combined schema for the homepage
  const combinedSchema = [
    organizationSchema,
    softwareApplicationSchema,
    webSiteSchema,
    serviceSchema,
    faqSchema(faqs)
  ];


  // Un usuario logueado no ve la landing: va a donde le corresponde. Si ya
  // completó el onboarding, al panel; si lo dejó por la mitad, a terminarlo.
  //
  // Antes solo se redirigía el primer caso, pero el `return null` de más abajo
  // se aplicaba a cualquier usuario logueado: quien había dejado el onboarding
  // sin terminar y volvía a la home veía una pantalla en blanco, sin nada para
  // clickear y sin forma de darse cuenta de que le faltaba terminar.
  useEffect(() => {
    if (loading || !currentUser || !userData) return;

    navigate(userData.onboardingCompleted ? '/admin/dashboard' : '/admin/onboarding', {
      replace: true,
    });
  }, [currentUser, userData, loading, navigate]);

  // Función para scroll suave con offset para el navbar.
  //
  // Si la inercia está activa, el salto lo tiene que hacer ella: usar
  // `window.scrollTo` mientras corre es pelearle: el navegador y la librería
  // mueven la misma página al mismo tiempo y el scroll tiembla.
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const navbarHeight = 80; // Altura aproximada del navbar
    const lenis = scrollSuave.current;

    if (lenis) {
      lenis.scrollTo(element, { offset: -navbarHeight });
      return;
    }

    // Sin inercia —en teléfono, o con "reducir movimiento" activado— vale el
    // salto de siempre.
    window.scrollTo({ top: element.offsetTop - navbarHeight, behavior: 'smooth' });
  };

  // Configuración del navbar para Aturno con navegación interna
  const navbarConfig = {
    logo: {
      url: "#inicio",
      src: "/logo-aturno.png",
      alt: "Aturno Logo",
      title: "Aturno",
      onClick: () => scrollToSection('inicio'),
    },
    menu: [
      {
        title: "Inicio",
        url: "#inicio",
        onClick: () => scrollToSection('inicio'),
      },
      {
        title: "Características",
        url: "#caracteristicas",
        onClick: () => scrollToSection('caracteristicas'),
      },
      {
        title: "Precios",
        url: "#precios",
        onClick: () => scrollToSection('precios'),
      },
      // Antes acá había un "Acerca de" que scrolleaba al CTA del final: no hay
      // ninguna sección sobre la empresa, así que el menú prometía algo que la
      // página no tiene. Las FAQ sí existen y no estaban en el menú, aunque el
      // footer ya las linkea como /#recursos.
      {
        title: "Preguntas",
        url: "#recursos",
        onClick: () => scrollToSection('recursos'),
      },
    ],
    mobileExtraLinks: [
      // Solo rutas que existen en App.js. Antes esto apuntaba a
      // /contacto, /soporte, /blog y /status, que no existen y que el
      // catch-all /:slug convertía en páginas de negocio rotas.
      { name: "Ver demo", url: "/demo" },
      { name: "Crear cuenta", url: "/auth/register" },
    ],
    auth: {
      login: { text: "Iniciar Sesión", url: "/auth/login" },
      signup: { text: "Registrarse", url: "/auth/register" },
    },
  };

  // Mientras el efecto de arriba redirige, pintar la landing sería un parpadeo.
  // Se exige `userData` a propósito: si por alguna razón no llegó, es mejor
  // mostrar la landing que dejar la pantalla vacía.
  if (!loading && currentUser && userData) {
    return null;
  }

  return (
    <AuroraBackground
      showRadialGradient={true}
      showGrid={false}
      showGlow={true}
      animated={true}
    >
      <SEO
        title="Sistema de Turnos Online para tu Negocio | Aturno - Software de Gestión"
        description="Software de gestión de turnos que automatiza las reservas de tu negocio. Tus clientes reservan online las 24 horas y un bot de WhatsApp les contesta solo. Probalo gratis 14 días."
        keywords="sistema de turnos online, software gestión turnos, agenda online negocio, app turnos clientes, sistema reservas automatico, gestión citas negocio, turnos online argentina, software turnos"
        url="https://aturno.app"
        image="/logo-aturno.png"
        type="website"
        schema={combinedSchema}
      />
      <div className="home">
        {/* Navbar de shadcn/ui */}
        <Navbar1 {...navbarConfig} />

        {/* Hero Section */}
        <main>
          {/* El hero es una tarjeta, no una franja a todo el ancho.
              Antes iba de punta a punta y sin fondo propio, así que se fundía
              con la sección de abajo y no se leía como un bloque. Ahora repite
              el contenedor que ya usa la barra de navegación —mismo max-w-6xl,
              mismo borde y mismo fondo translúcido— y la página queda como
              bloques apilados. Redondeo grande y no `rounded-full` como la
              barra: en un bloque alto la pastilla comería las esquinas. */}
          {/* `px-3 sm:px-4` son los mismos que la barra de navegación, para que
              los dos bloques queden alineados por el costado. Con px-4 el hero
              entraba 4px más adentro que la barra y en un teléfono se notaba.

              El `pt` de acá es la separación entre la barra y la tarjeta, y va
              igual que el `pt` de la barra contra el borde de la ventana: así
              los tres huecos —ventana/barra, barra/tarjeta— miden lo mismo.
              Eran 96px, que sumados a los 80 de relleno interno daban 177px de
              aire entre la barra y la primera línea de texto. */}
          {/* Quien ocupa la pantalla al entrar es esta zona, no la tarjeta: la
              tarjeta sigue midiendo lo que mide su contenido y queda flotando
              en el medio, con aire alrededor. Antes esta altura estaba puesta
              en la tarjeta, que se estiraba hasta los bordes y dejaba apenas
              16px de margen.

              Se descuentan los 86/90px que ocupa la barra de navegación (la
              pastilla mide 74 y va separada 12 o 16 del borde según el
              tamaño), así que lo que queda es exactamente el resto de la
              ventana.

              `svh` y no `vh`: en el navegador del teléfono `vh` mide la ventana
              SIN las barras del sistema, y la zona quedaría más alta que lo que
              se ve de verdad. `svh` es la medida chica, la que garantiza que
              entre.

              `min-height` y no `height`: en una pantalla baja el contenido
              manda y la zona crece, en vez de recortar el hero.

              `items-start` y no `items-center`: centrada, la tarjeta bajaba
              100px en un monitor y quedaba despegada de la barra. Va arriba, y
              el sobrante de la ventana queda abajo como fondo.

              El `pt` es mayor que el `pb` a propósito. Pegada del todo a la
              barra (16px) quedaba muy arriba; centrada, 101px, muy abajo; 48
              todavía se leía alta. Queda en 72px en escritorio y 48 en
              teléfono, y es el único número a mover si hay que ajustarlo. */}
          <section
            id="inicio"
            className="flex min-h-[calc(100svh-86px)] items-start px-3 pb-3 pt-12 sm:min-h-[calc(100svh-90px)] sm:px-4 sm:pb-4 sm:pt-[4.5rem]"
          >
            <div className="relative mx-auto w-full max-w-6xl overflow-hidden rounded-3xl border border-gray-200/70 bg-white/80 px-4 pb-14 pt-12 shadow-sm backdrop-blur sm:px-6 sm:pb-16 sm:pt-14">
              <EmojisFlotantes />
              <div className="relative mx-auto flex w-full max-w-3xl flex-col items-center text-center">
              {/* El hero entra por partes, de arriba hacia abajo. Los `orden`
                  son consecutivos para que se lea como una sola cascada y no
                  como cinco cosas apareciendo a destiempo.
                  `desplazamiento` corto: es lo primero que se ve al entrar y un
                  salto largo acá se siente lento, no elegante. */}
              <Aparecer
                como="span"
                orden={0}
                desplazamiento={12}
                className="mb-8 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white/90 px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm backdrop-blur"
              >
                <StarIcon />
                El software que tu negocio necesita
              </Aparecer>

              <Aparecer orden={1} desplazamiento={12} className="flex w-full flex-col items-center">
              <LayoutGroup>
                <motion.div className="flex w-full flex-col items-center" layout>
                  <motion.h1
                    className="text-balance text-center text-4xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl"
                    layout
                    transition={{ type: "spring", damping: 30, stiffness: 400 }}
                  >
                    Gestión de turnos más
                  </motion.h1>
                  <TextRotate
                    texts={[
                      "Automático 🤖",
                      "Profesional 💼",
                      "Organizado 📋",
                      "Eficiente ⚡"
                    ]}
                    mainClassName="mt-3 max-w-full overflow-hidden rounded-2xl bg-primary px-4 py-2 text-4xl font-bold leading-tight text-primary-foreground sm:px-6 sm:py-3 sm:text-5xl lg:text-6xl justify-center"
                    staggerFrom={"last"}
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "-120%" }}
                    staggerDuration={0.025}
                    splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1"
                    transition={{ type: "spring", damping: 30, stiffness: 400 }}
                    rotationInterval={2000}
                  />
                </motion.div>
              </LayoutGroup>
              </Aparecer>

              <Aparecer como="p" orden={2} desplazamiento={12} className="mt-8 max-w-xl text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg" role="doc-subtitle">
                Dejá que tus clientes reserven turnos online mientras vos te enfocás en hacer crecer tu negocio. Sistema completo de gestión de turnos y reservas.
              </Aparecer>

              <Aparecer orden={3} desplazamiento={12} className="mt-10 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button asChild size="cta">
                  <Link to="/auth/register">Probá Gratis Ahora</Link>
                </Button>
                <Button asChild size="cta" variant="outline">
                  <Link to="/demo">Ver Demo</Link>
                </Button>
              </Aparecer>

              <Aparecer como="p" orden={4} desplazamiento={12} className="mt-6 text-sm text-neutral-500">
                Configuración en 5 minutos · Prueba gratis · Sin tarjeta de crédito
              </Aparecer>
              </div>
            </div>
          </section>

        {/* Magic UI Demo Section */}
        {/* Esta sección y la del panel comparten el mismo `bg-neutral-50` y van
            pegadas, así que se leen como UNA sola franja gris sobre el fondo de
            la página. Por eso el redondeo va sólo en las puntas del bloque: acá
            arriba y abajo de la del panel.

            Redondear también el borde de abajo de esta —o el de arriba de la
            otra— marcaría la costura del medio, que es justo lo que no existe:
            se vería un pellizco en la mitad de una franja que tiene que parecer
            continua.

            La sombra sigue la misma regla y por eso apunta sólo hacia ARRIBA
            (offset -12). El `spread` de -16 es lo que la mantiene lejos de la
            costura: achica la caja de la sombra 16px por lado antes de
            desenfocarla, así que por abajo termina 14px ANTES del borde de esta
            sección y nunca asoma sobre la del panel. Sin eso, las dos sombras
            se cruzarían en el medio de la franja y dibujarían justamente la
            línea que el redondeo continuo está evitando. */}
        <section
          id="caracteristicas"
          className="rounded-t-3xl bg-neutral-50 px-4 py-20 shadow-[0_-12px_30px_-16px_rgba(0,0,0,0.13)] sm:rounded-t-[2.5rem] sm:px-6 sm:py-24"
        >
          <div className="mx-auto max-w-7xl">
            {/* Header */}
            <Aparecer como="header" className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="text-balance text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
                Todo lo que necesitás para gestionar tu negocio
              </h2>
              <p className="mt-4 text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg">
                Ahorrá tiempo, reducí ausencias y dejá que tus clientes reserven solos
              </p>
            </Aparecer>

            <div className="mb-16">
              <MosaicoFuncionalidades />
            </div>

            <div className="grid grid-cols-1 items-start gap-8">

              {/* Rubros — reemplaza al globo 3D, que no comunicaba nada
                  comercial y además fallaba cuando no hay WebGL. */}
              <article className="space-y-6">
                <Aparecer como="header" className="text-center">
                  <h3 className="text-xl font-semibold text-neutral-900 sm:text-2xl">
                    Se adapta a tu rubro
                  </h3>
                  <p className="mt-2 text-neutral-600">
                    Negocios y profesionales que ya usan Aturno
                  </p>
                </Aparecer>

                <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {rubros.map(({ id, name, description, icon }) => (
                    <li
                      key={id}
                      className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition-colors hover:border-neutral-300 hover:bg-neutral-50"
                    >
                      <span className="emoji-flag shrink-0 text-3xl leading-none" aria-hidden="true">
                        {icon}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-neutral-900">{name}</p>
                        <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                          {description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* El panel, después de contar qué hace la herramienta y antes de hablar
            de plata: cierra la sección de funcionalidades mostrando de una vez
            todo lo que se acaba de enumerar. */}
        {/* El redondeo de abajo cierra la franja gris que arranca en
            #caracteristicas. Va sin `overflow-hidden` a propósito: la tarjeta
            del panel tiene una transformación 3D, y recortar eso obliga al
            navegador a rehacer el recorte en cada cuadro del scroll (ver el
            comentario de PanelEnPerspectiva). No hace falta igual: el redondeo
            afecta al fondo de la sección, y ningún hijo pinta fondo propio en
            las esquinas. */}
        <section
          aria-label="Vista del panel de Aturno"
          className="rounded-b-3xl bg-neutral-50 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.13)] sm:rounded-b-[2.5rem]"
        >
          <PanelEnPerspectiva
            encabezado={
              <>
                <h2 className="text-balance text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
                  Un panel para manejar todo
                </h2>
                <p className="mx-auto mt-5 max-w-xl text-pretty text-lg leading-relaxed text-neutral-600 sm:text-xl">
                  Turnos, clientes, servicios, personal y horarios en un solo
                  lugar, desde la computadora o el teléfono
                </p>
              </>
            }
          >
            {/* `width` y `height` son las medidas REALES del archivo, no una
                preferencia: el navegador las usa para reservar el espacio antes
                de descargarlo, y si no coinciden la página salta al cargar.
                Quedaron en 1917×943 —las de la captura anterior— hasta que se
                reemplazó el archivo, así que si volvés a cambiar la imagen,
                cambialas también.

                El marco de PanelEnPerspectiva es ~1.65:1 y esta captura es
                1.55:1, así que `object-cover` la escala por el ancho y recorta
                unos 40px de abajo: justo el blanco del final de la lista. */}
            <img
              src="/dashboard.png"
              alt="Panel de Aturno mostrando la agenda de turnos del día"
              width={1382}
              height={894}
              draggable={false}
              className="h-full w-full rounded-2xl object-cover object-left-top"
            />
          </PanelEnPerspectiva>
        </section>

        {/* Pricing Section */}
        {/* Planes.
            Es la vista de marketing: los botones llevan a registrarse y no
            disparan ningún cobro. El selector transaccional sigue siendo
            components/pricing-section.jsx, que usan /admin/plans y el
            paso 10 del onboarding. */}
        <section id="precios" className="px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-6xl">
            <Aparecer como="header" className="mb-14 max-w-2xl">
              <h2 className="text-balance text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
                Planes
              </h2>
              <p className="mt-3 text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg">
                Elegí el que mejor se ajuste a tu negocio. Podés cambiarlo o cancelarlo
                cuando quieras.
              </p>
            </Aparecer>

            {/* Los mismos tres planes que el onboarding y /admin/plans, de
                data/planes.js. Acá se listaba `planes`, que son cuatro —los tres
                pagos con los nombres viejos más la tarjeta de la prueba—, así
                que en una grilla de tres columnas la cuarta caía sola abajo, y
                encima anunciaba un plan con un nombre distinto del que después
                aparecía al pagar. La prueba de 14 días se sigue ofreciendo en el
                hero y en el cierre de la página. */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {planesDeContratacion.map((plan, i) => (
                /* La tarjeta vive en components/business/TarjetaDePlan.jsx: la
                   misma que usa el selector con el que se paga, para que la
                   landing y el producto no se vean como dos cosas distintas.
                   `Aparecer` va por fuera y no adentro de la tarjeta para no
                   tocar un componente que también usa el selector de pago. */
                <Aparecer key={plan.id} orden={i} className="flex">
                <TarjetaDePlan plan={plan} className="w-full">
                  {/* El plan cotizado no se registra solo: en el backend es
                      `cobrable: false`, así que mandarlo a /auth/register lo
                      dejaría en un alta que no puede terminar en nada. Se saca
                      un turno con nosotros, en nuestra propia página pública
                      (`plan.enlace`), que además es la demo del producto. */}
                  {plan.cotizado ? (
                    <Button asChild variant="outline" className="w-full">
                      <Link to={plan.enlace}>{plan.cta}</Link>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      variant={plan.destacado ? 'default' : 'outline'}
                      className="w-full"
                    >
                      <Link to="/auth/register">{plan.cta}</Link>
                    </Button>
                  )}
                </TarjetaDePlan>
                </Aparecer>
              ))}
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <Aparecer id="recursos">
          <Faq2 />
        </Aparecer>
        
        {/* El CTA de cierre se mudó al footer (src/data/navegacion.js): estaba
            acá, pegado al footer, y era el mismo bloque que /demo y
            /admin/plans no tenían. Ahora sale del mismo lugar que el resto de
            la navegación y aparece en las tres páginas. Conserva id="empezar"
            por si algo lo enlaza. */}
        </main>
        
        {/* Footer */}
        {/* `mostrarEquipos` sólo acá: en /demo y /admin/plans el cierre va sin
            la maqueta. Ver el comentario de Footer. */}
        <Footer {...footerData} mostrarEquipos />
      </div>
    </AuroraBackground>
  );
};

export default Home; 